import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { scopesApiDatasource, type Scope } from './scopes-api.datasource';

/**
 * Scopes da plataforma. Cache longo porque a lista é praticamente estática —
 * um scope novo é evento de configuração, não de operação.
 */
export function useScopes(enabled = true) {
  return useQuery({
    queryKey: ['identity', 'scopes'],
    queryFn: () => scopesApiDatasource.listScopes(),
    staleTime: 10 * 60 * 1000,
    enabled,
  });
}

/**
 * As plataformas que o iamEditor administra — as únicas que o seletor dele
 * oferece. Sem cache longo: muda quando alguém concede ou tira uma role dele.
 * `enabled` porque o admin não a chama: para ele, a fonte é `useScopes`.
 */
export function useProvisionableScopes(enabled: boolean) {
  return useQuery({
    queryKey: ['identity', 'provisionable-scopes'],
    queryFn: () => scopesApiDatasource.listProvisionableScopes(),
    enabled,
  });
}

/**
 * Rótulo legível de um scope: o `name` que o back manda. Só cai no id se a
 * resposta vier sem nome, o que seria defeito de contrato, não estado normal.
 */
export function scopeOptionLabel(scope: Scope): string {
  return scope.name || scope.id;
}

/**
 * Formatadores de scope alimentados pela lista do back.
 *
 * É um hook (e não funções puras) porque os rótulos vêm de uma request: não há
 * como resolver id → nome sem os dados carregados. Enquanto a lista não chega —
 * ou se a request falhar — cada formatador cai no próprio id, que continua
 * sendo informação verdadeira.
 */
export function useScopeFormatter() {
  const { data: scopes } = useScopes();

  const scopeLabel = useCallback(
    (id: string): string | undefined => {
      const scope = scopes?.find((s) => s.id === id);
      return scope ? scopeOptionLabel(scope) : undefined;
    },
    [scopes],
  );

  /** `"label (id)"`, ou só o id enquanto o rótulo não é conhecido. */
  const formatScope = useCallback(
    (id: string): string => {
      const label = scopeLabel(id);
      return label ? `${label} (${id})` : id;
    },
    [scopeLabel],
  );

  /** Um `"label (id)"` por linha — útil em tooltips. */
  const formatScopeList = useCallback(
    (ids: string[]): string => ids.map(formatScope).join('\n'),
    [formatScope],
  );

  return { scopeLabel, formatScope, formatScopeList };
}
